import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { shallowEqual } from '@pepper/utils/src/function/misc'
import { fetchProjectTemplateList } from '@pepper/utils/src/api/projects/projects'
import { SetQuery } from 'use-query-params'

import { useShowMessage } from '../providers'

type ReturnType = {
  records: ProjectTemplateList[]
  totalRecords: number
  currentPage: number
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  handlePageChange: (page: number) => void
  error: Error | null
}

type DataModal = {
  records: ProjectTemplateList[]
  total_records: number
}

export const useProjectDraftList = (
  index: any,
  query_params: Partial<ProjectQueryParams>,
  page_size = 6,
  current_page = 0,
  setUrlParams?: SetQuery<any>
): ReturnType => {
  const showMessage = useShowMessage()
  const [pagination, setPagination] = useState({
    current_page: 0,
    page_size
  })
  const isInitialMount = useRef(true)

  // storing the previous filter query param for resetting the pagination whenever it the filter changes
  const previousValue = useRef<Record<string, any>>({})

  const handlePageChange = (current_page: number) => {
    setPagination({ ...pagination, current_page })

    setUrlParams &&
      setUrlParams(
        { filters: query_params, currentPage: current_page },
        'replaceIn'
      )
  }

  useEffect(() => {
    // when component is mounted set filters/sort & pagination from the url params
    if (isInitialMount.current) {
      handlePageChange(current_page)
      previousValue.current = query_params
      isInitialMount.current = false
      setUrlParams && setUrlParams({ filters: query_params }, 'replaceIn')
      return
    }

    // if filter/sort is change then change the pagination to the first page
    if (!shallowEqual(query_params, previousValue.current)) {
      handlePageChange(0)
      setUrlParams && setUrlParams({ filters: query_params }, 'replaceIn')
      previousValue.current = query_params
    }
  }, [query_params])

  const handleApiRequest = async () => {
    const { status, data, error } = await fetchProjectTemplateList({
      query_params,
      page: pagination.current_page,
      page_size: pagination.page_size,
      type: 'draft'
    })
    if (status === 'ok') {
      return data
    } else {
      throw new Error(error)
    }
  }

  const { isLoading, isFetching, isError, error, refetch, data } = useQuery<
    DataModal,
    Error
  >({
    queryKey: [index, pagination.current_page, query_params],
    queryFn: handleApiRequest,
    enabled: index ? true : false
  })

  useEffect(() => {
    if (isError) {
      showMessage({ type: 'error', message: error?.message })
    }
  }, [isError, error, showMessage])

  return {
    records: data?.records ?? [],
    totalRecords: data?.total_records ?? 0,
    currentPage: pagination.current_page,
    isLoading,
    isFetching,
    handlePageChange,
    refetch,
    error
  }
}
